import { safeSetDoc, safeUpdateDoc } from '../utils';
import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { appId, TAG_HIERARCHY as OLD_DEFAULT } from '../constants';

const ROOT_TAGS = ['Food', 'Drink', 'Coffee', 'Sight', 'Music Venue', 'Shopping'];

function convertOldToDict(oldH) {
  const dict = {};
  
  ROOT_TAGS.forEach((root, idx) => {
    const id = `tag_${root.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
    dict[id] = { id, name: root, parentId: null, sortOrder: idx, isRoot: true, isSpecial: false };
  });

  let counter = 100;

  const processNode = (node, parentId) => {
    let order = 0;
    for (const [name, children] of Object.entries(node)) {
      let id = Object.values(dict).find(t => t.name === name && t.isRoot)?.id;
      
      if (!id) {
         id = `tag_custom_${Date.now()}_${counter++}`;
         dict[id] = { id, name, parentId, sortOrder: order++, isRoot: false, isSpecial: false };
      } else if (parentId && !dict[id].isRoot) {
         dict[id].parentId = parentId;
      }
      processNode(children, id);
    }
  };

  processNode(oldH, null);
  return dict;
}

function buildHierarchyFromDict(dict) {
  const items = Object.values(dict).sort((a, b) => a.sortOrder - b.sortOrder);
  
  const buildNode = (parentId) => {
    const nodeObj = {};
    const children = items.filter(t => t.parentId === parentId);
    for (const child of children) {
      nodeObj[child.name] = buildNode(child.id);
    }
    return nodeObj;
  };
  
  const hierarchy = {};
  const roots = items.filter(t => t.parentId === null);
  for (const root of roots) {
    hierarchy[root.name] = buildNode(root.id);
  }
  return hierarchy;
}

export function useTags() {
  const [tagDictionary, setTagDictionary] = useState({});
  const [tagsHierarchy, setTagsHierarchy] = useState({});
  const [specialTags, setSpecialTags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'tags', 'hierarchy');
    
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.dictionary) {
          let dict = { ...data.dictionary };
          let changed = false;

          // Enforce Root Categories and rename Plurals to Singulars
          ROOT_TAGS.forEach((rootName, idx) => {
            const existing = Object.values(dict).find(t => t.isRoot && (
              t.name.toLowerCase() === rootName.toLowerCase() ||
              t.name.toLowerCase() === rootName.toLowerCase() + 's'
            ));

            if (existing) {
              if (existing.name !== rootName) {
                dict[existing.id] = { ...existing, name: rootName };
                changed = true;
              }
            } else {
              const id = `tag_root_${Date.now()}_${idx}`;
              dict[id] = { id, name: rootName, parentId: null, sortOrder: Object.values(dict).filter(t => t.isRoot).length, isRoot: true, isSpecial: false };
              changed = true;
            }
          });

          // Aggressive cleanup: delete any stray roots that were left behind (e.g. "Drinks", "Sights", "Want To Go")
          const toDelete = Object.values(dict).filter(t => 
            t.parentId === null && (t.name === 'Drinks' || t.name === 'Sights' || t.name === 'Want To Go')
          );
          if (toDelete.length > 0) {
            toDelete.forEach(t => { delete dict[t.id]; });
            changed = true;
          }

          if (changed) {
            safeUpdateDoc(docRef, { dictionary: dict });
          }

          setTagDictionary(dict);
          setTagsHierarchy(buildHierarchyFromDict(dict));
          setSpecialTags(Object.values(dict).filter(t => t.isSpecial).map(t => t.name));
        } else if (data.hierarchy) {
          // Migrate old nested format to flat dictionary
          const dict = convertOldToDict(data.hierarchy);
          safeUpdateDoc(docRef, { dictionary: dict });
        }
      } else {
        // Initialize new database with root tags
        const dict = convertOldToDict(OLD_DEFAULT || {});
        safeSetDoc(docRef, { dictionary: dict });
      }
      setLoading(false);
    }, (error) => {
      console.error("Error fetching tags dictionary:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const saveTagDictionary = async (newDict) => {
    try {
      const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'tags', 'hierarchy');
      await safeUpdateDoc(docRef, { dictionary: newDict });
    } catch (e) {
      console.error("Error saving tags dictionary:", e);
      throw e;
    }
  };

  const extractTags = (hierarchy) => {
    let tags = [];
    for (const [key, children] of Object.entries(hierarchy)) {
      tags.push(key);
      if (Object.keys(children).length > 0) {
        tags = tags.concat(extractTags(children));
      }
    }
    return tags;
  };

  const availableTags = Array.from(new Set(extractTags(tagsHierarchy)));

  const getParentTags = (targetTag) => {
    const search = (node, path) => {
      for (const [key, children] of Object.entries(node)) {
        if (path.includes(key)) continue;
        if (key === targetTag) return path;
        if (Object.keys(children).length > 0) {
          const result = search(children, [...path, key]);
          if (result) return result;
        }
      }
      return null;
    };
    return search(tagsHierarchy, []) || [];
  };

  const getChildTags = (targetTag) => {
    let foundNode = null;
    
    const findNode = (node, target) => {
      if (!node) return false;
      for (const [key, children] of Object.entries(node)) {
        if (key === target) {
          foundNode = children;
          return true;
        }
        if (findNode(children, target)) return true;
      }
      return false;
    };
    
    findNode(tagsHierarchy, targetTag);
    
    if (!foundNode) return [];
    return extractTags(foundNode);
  };

  return { 
    tagDictionary, 
    tagsHierarchy, 
    specialTags, 
    availableTags, 
    getParentTags, 
    getChildTags, 
    saveTagDictionary, 
    loading 
  };
}
